import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActivationStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Destroyable } from 'src/app/shared/services/destroyable';
import {
  isValidShimmerKey,
  ShimmerRouteKey,
} from '../../constants/shimmer-keys';
import { ShimmerService } from '../../services/shimmer.service';

@Component({
  selector: 'app-shimmer-router-outlet',
  templateUrl: './shimmer-router-outlet.component.html',
  styleUrls: ['./shimmer-router-outlet.component.scss'],
})
export class ShimmerRouterOutletComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  private _key: ShimmerRouteKey | null = null;
  private isBackNavigationActive = false;

  get key(): ShimmerRouteKey | null {
    return this._key;
  }

  constructor(private shimmerService: ShimmerService, private router: Router) {
    super();
  }

  private isBackNavigation(event: NavigationStart): boolean {
    return event.navigationTrigger === 'popstate';
  }

  private initializeShimmerState(key: ShimmerRouteKey): void {
    this.shimmerService.loading$(key).subscribe();
  }

  private handleActivationStart(event: ActivationStart): void {
    this.clearExistingKey();
    if (!this.isBackNavigationActive) {
      this.updateShimmerKey(event.snapshot.data);
    }
  }

  private clearExistingKey(): void {
    if (this.key) {
      this.shimmerService.removeState(this.key);
      this._key = null;
    }
  }

  private updateShimmerKey(data: any): void {
    const shimmerKey = data?.['shimmerKey'];
    if (shimmerKey && isValidShimmerKey(shimmerKey)) {
      this._key = shimmerKey as ShimmerRouteKey;
      this.initializeShimmerState(this._key);
      if (!this.isBackNavigationActive) {
        this.shimmerService.show(this._key);
      }
    } else {
      this.clearExistingKey();
    }
  }

  private handleNavigationEvent(event: any): void {
    if (event instanceof NavigationStart) {
      this.isBackNavigationActive = this.isBackNavigation(event);
      if (this.key && !this.isBackNavigationActive) {
        this.shimmerService.show(this.key);
      }
    } else if (
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      this.hideAndClearShimmerState();
    } else if (event instanceof NavigationEnd) {
      requestAnimationFrame(() => {
        this.hideAndClearShimmerState();
      });
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError ||
            event instanceof ActivationStart
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (event) => {
          if (event instanceof ActivationStart) {
            this.handleActivationStart(event);
          } else {
            this.handleNavigationEvent(event);
          }
        },
        error: (_) => {
          this.hideAndClearShimmerState();
        },
      });
  }

  private hideAndClearShimmerState(): void {
    if (this.key) {
      this.shimmerService.hide(this.key);
      this.clearExistingKey();
      this.isBackNavigationActive = false;
    }
  }

  ngOnDestroy(): void {
    this.hideAndClearShimmerState();
    this._key = null;
    this.isBackNavigationActive = false;
    this.cleanup();
  }
}
